import React, { useEffect, useState } from "react";
import { Box, Container, Divider, Modal, Typography } from "@mui/material";
import ProductFilterForm from "components/ProductFilterForm";
import ProductTable from "components/ProductTable";
import apiService, { toastError, toastSuccess } from "services/apiService";
import Navbar from "components/Navbar";
import SettingsPage from "scenes/settings";
import ProductPriceForm from "components/ProductPriceForm";
import FlexBetween from "components/FlexBetween";
import { useDispatch, useSelector } from "react-redux";
import { setDatas } from "state";

const HomePage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isPriceSyncLoading, setIsPriceSyncLoading] = useState(false);
  const [priceSyncError, setPriceSyncError] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const dispatch = useDispatch();
  const filters = useSelector((state) => state.filters);
  const productDatas = useSelector((state) => state.datas);

  const parseNumericValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return null;
    }
    const parsed = Number(String(value).replace(",", "."));
    return Number.isNaN(parsed) ? null : parsed;
  };

  useEffect(() => {
    // Fetch products based on filters
    const fetchProducts = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await apiService.postData(
          "/atemo/search",
          filters
        );

        if (error) {
          console.error("Erreur lors de la récupération des produits :", error);
          setError(error);
          dispatch(setDatas({ datas: null }));
          return;
        }

        setError(null);
        dispatch(setDatas({ datas: data }));
      } catch (error) {
        console.error("Erreur lors de la récupération des produits :", error);
        setError(error.message || "Une erreur est survenue");
        dispatch(setDatas({ datas: null }));
      } finally {
        setIsLoading(false);
      }
    };

    if (Object.keys(filters).length > 0) {
      fetchProducts();
    }
  }, [filters, dispatch]);

  const handleChangeSelection = (products) => {
    setSelectedProducts(products);
  };

  const handleValidatePriceUpdates = async (rows) => {
    setIsPriceSyncLoading(true);
    const { error } = await apiService.postData("/atemo/syncPrices", {
      products: rows,
    });

    if (error) {
      setPriceSyncError(error);
      console.error(error);
      toastError(error);
    } else {
      setPriceSyncError(null);
      toastSuccess("Prix mis à jour avec succès");
    }
    setIsPriceSyncLoading(false);
  };

  const handlePriceFormSubmit = (formValues) => {
    if (!productDatas.pieces) {
      return;
    }
    if (!productDatas.pieces.length) {
      return;
    }
    if (!selectedProducts || !selectedProducts.length) {
      return;
    }

    const [activeField, rawValue] =
      Object.entries(formValues).find(
        ([, value]) => value !== null && value !== undefined && value !== ""
      ) ?? [];

    if (!activeField) {
      return;
    }

    const inputValue = parseNumericValue(rawValue);

    if (inputValue === null) {
      return;
    }

    const computeBasePrice = (row) => {
      const parsed = parseNumericValue(row.PrixVenteTTC);
      if (parsed !== null) {
        return parsed;
      }

      return null;
    };

    const formatPrice = (value) =>
      Number.isFinite(value) ? value.toFixed(2) : "";

    let newProductDatas = {
      ...productDatas,
      pieces: productDatas.pieces.map((p) => ({ ...p })),
    };
    newProductDatas.pieces.forEach((p) => {
      if (
        selectedProducts.includes(p.Identifiant) ||
        selectedProducts.includes(`${p.Identifiant}`)
      ) {
        if (activeField === "custom") {
          p.newPrice = formatPrice(inputValue);
          return;
        }

        const basePrice = computeBasePrice(p);
        if (basePrice === null) {
          return;
        }

        switch (activeField) {
          case "remiseEuro":
            p.newPrice = formatPrice(Math.max(basePrice - inputValue, 0));
            break;
          case "remisePercent":
            p.newPrice = formatPrice(
              Math.max(basePrice * (1 - inputValue / 100), 0)
            );
            break;
          case "augmentationEuro":
            p.newPrice = formatPrice(basePrice + inputValue);
            break;
          case "augmentationPercent":
            p.newPrice = formatPrice(basePrice * (1 + inputValue / 100));
            break;
          default:
            return;
        }
      }
    });

    dispatch(setDatas({ datas: newProductDatas }));
  };

  return (
    <Box sx={{ height: "100vh", overflowY: "auto" }}>
      <Navbar displaySettings handleClickSettings={() => setOpenModal(true)} />
      <Container sx={{ py: 3 }}>
        <FlexBetween
          gap={1}
          flexWrap="wrap"
          sx={{
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              flex: 1,
              border: "1px solid",
              borderColor: "primary.main",
              borderRadius: 2,
              p: 2,
              minWidth: "300px",
            }}
          >
            <Typography variant="h4" sx={{ mb: 4 }}>
              🔎 Recherche
            </Typography>
            <ProductFilterForm
              isLoading={isLoading}
              fetchingError={(error) => setError(error)}
            />
            {error && (
              <Typography color="error" sx={{ mt: 2 }}>
                {error}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              flex: 1,
              border: "1px solid",
              borderColor: "primary.main",
              borderRadius: 2,
              p: 2,
              minWidth: "300px",
            }}
          >
            <Typography variant="h4" sx={{ mb: 4 }}>
              📈 Affectation des prix
            </Typography>
            <ProductPriceForm
              enableSubmit={selectedProducts?.length}
              onSubmit={handlePriceFormSubmit}
            />
          </Box>
        </FlexBetween>
        <Divider sx={{ my: 3 }} />

        {productDatas && (
          <ProductTable
            isLoading={isLoading}
            isPriceSyncLoading={isPriceSyncLoading}
            onChangeSelection={handleChangeSelection}
            onValidate={handleValidatePriceUpdates}
          />
        )}

        {priceSyncError && (
          <Typography color="error" sx={{ mt: 2 }}>
            {priceSyncError}
          </Typography>
        )}
      </Container>
      <Modal
        open={openModal}
        onClose={() => {
          setOpenModal(false);
        }}
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description"
      >
        <Box
          sx={{
            width: 400,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "primary.main",
            borderRadius: "2px",
            boxShadow: 24,
            pt: 2,
            px: 4,
            pb: 3,
          }}
        >
          <SettingsPage />
        </Box>
      </Modal>
    </Box>
  );
};

export default HomePage;
